import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PlatformService, UserService } from '@evc/platform';
import { utDpuVersion } from '@root/package.json';
import { combineLatestWith, concat, filter, map, Observable, Subscription, switchMap, take, toArray } from 'rxjs';

import {
  CanvasCommunicationService,
  ConnectionStatusBar,
  DevicesConnectionStatusBar,
  DevicesService,
  DpuStateKey,
  EncoderPositionStatusBar,
  FeatureFlagsConfigService,
  FileNameStatusBar,
  InspectionModeStatusBar,
  LayoutManagerService,
  LayoutService,
  MaxScanSpeedStatusBar,
  PaletteService,
  ParametersInfosService,
  ReleaseNotesComponent,
  RemoteControlledStatusBar,
  RemoteControlService,
  ScannerService,
  StatusbarService,
} from '@olympus/ngx-shared';
import { DialogService } from 'shared-modules-angular/dialog';
import { InspectionMode } from 'ut-business/inspection-mode';
import { GroupModel, UtSettingsModel } from 'ut-business/models';
import { License } from 'ut-business/types';

import { WindViewPreference } from '../../core/app.preference';
import { WindViewState } from '../../core/app.state';
import { ConfigService } from '../../core/services/config';
import { LicenseStatusIndicator } from '../../modules/license/services/cryptlex-license.service';
import { LicenseFacadeService } from '../../modules/license/services/license-facade.service';
import { QUICK_ACTIONS_RIBBON_PADDING } from '../../modules/ribbon/ribbon.constants';
import { OscCloudService } from '../services/osc-cloud/osc-cloud.service';

@Component({
  selector: 'app-default',
  templateUrl: './default.component.html',
  styleUrls: ['./default.component.scss'],
  standalone: false,
})
export class DefaultComponent implements OnInit, OnDestroy {
  public readonly VERSION: string;

  private readonly _licenseService = inject(LicenseFacadeService);

  private readonly _subscription: Subscription = new Subscription();

  private readonly _evcPlatformService: PlatformService | null = null;
  private readonly _userService: UserService | null = null;

  public initials: string;
  public avatarUrl: string | undefined;
  public isCloudConnected: boolean;
  public layoutManagerIsActive = false;
  public isFileLoaded = false;
  public canShowZoneHistogram = false;
  public canShowAnalysisMenu = false;
  public licenseType: License;
  public featureFlagEncoder = false;
  public featureFlagAnalysis: boolean;
  public featureFlagCScanThickness = false;
  public featureFlagTilingCanvas = true;
  public utSettingsModel: UtSettingsModel | undefined;
  public showReleaseNotes = false;
  public showLayout = false;
  public ribbonPaddingLeft = 0;
  public licenseStatusIndicator$: Observable<LicenseStatusIndicator> = this._licenseService.statusIndicator$;

  public get appName(): string {
    return this._featureFlagsConfigService.appConfig.appName;
  }

  public get appIcon(): string {
    return this._featureFlagsConfigService.appConfig.appIcon;
  }

  constructor(
    private readonly _detector: ChangeDetectorRef,
    private readonly _config: ConfigService,
    private readonly _preference: WindViewPreference,
    private readonly _state$: WindViewState,
    private readonly _statusbar: StatusbarService,
    private readonly _paletteService: PaletteService,
    private readonly _communicationService: CanvasCommunicationService,
    private readonly _remoteControlService: RemoteControlService,
    private readonly _layoutManagerService: LayoutManagerService,
    private readonly _layoutService: LayoutService,
    private readonly _cloud: OscCloudService,
    private readonly _dialogService: DialogService,
    private readonly _featureFlagsConfigService: FeatureFlagsConfigService,
    private readonly _scannerService: ScannerService,
    private readonly _paramsInfoService: ParametersInfosService,
    private readonly _devicesService: DevicesService,
  ) {
    if (_config.isEvidentConnect && _config.isCloudConnected) {
      this._evcPlatformService = inject(PlatformService);
      this._userService = inject(UserService);
    }

    this.isCloudConnected = this._config.isCloudConnected;
    this.licenseType = this._config.licenseType;
    this.featureFlagEncoder = this._featureFlagsConfigService.getFeature('scannerMenu');
    this.featureFlagAnalysis = this._featureFlagsConfigService.getFeature('analysis');
    this.featureFlagCScanThickness = this._featureFlagsConfigService.getFeature('cScanThickness');
    this.featureFlagTilingCanvas = this._featureFlagsConfigService.getFeature('tilingCanvas');

    this.VERSION = this.getVersion();
    this._state$.patch('AppName', this.appName);
    this._state$.patch('AppVersion', this._config.version.windView);
    this._state$.patch('AppNameVersion', this.VERSION);
    this.initials = this.getInitials();
  }

  public ngOnInit(): void {
    this._subscription.add(
      this._licenseService.featuresChanged$.subscribe(() => this.setAppFeatures()),
    );

    this._licenseService.ensureLicensed().pipe(take(1)).subscribe(() => {
      this.init();
    });
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public handlerLayoutChange(): void {
    this._layoutService.reloadViews();
  }

  public openLicenseModal(): void {
    this._licenseService.openLicenseModal();
  }

  public showReleaseNotesDialog(): void {
    this._dialogService.open(
      ReleaseNotesComponent,
      {
        height: ReleaseNotesComponent.HEIGHT,
        width: ReleaseNotesComponent.WIDTH,
        hasBackdrop: false,
      },
    );
  }

  public onQuickActionsToggled(event: { opened: boolean, width: number }): void {
    this.ribbonPaddingLeft = event.opened ? event.width - QUICK_ACTIONS_RIBBON_PADDING : 0;
  }

  private init(): void {
    this.showLayout = true;

    if (this._evcPlatformService && this._userService) {
      this._evcPlatformService.ready$.pipe(
        combineLatestWith(this._userService.ready$),
        filter(([evcReady, userReady]) => !!evcReady && !!userReady),
        take(1),
      ).subscribe(() => {
        this.initials = this.getInitials();
        this.avatarUrl = this.getAvatarUrl();
      });
    }

    this.initEventListener();
    this.initStatusBar();
    this.initDpuFile();
    this.initPalette();
    this.initLayoutAndCanvas();
    this.initQuickActionsParams();
  }

  private initQuickActionsParams(): void {
    this._subscription.add(this._communicationService.context.activeGroupId$.pipe(
      map(() => this._communicationService.context.activeGroupModel),
      switchMap((activeGroupModel: GroupModel) => activeGroupModel.utSettingsModel.utSettingsModel$),
    ).subscribe((model: UtSettingsModel) => {
      this.utSettingsModel = model;
    }));
  }

  private setAppFeatures(): void {
    this.featureFlagCScanThickness = this._featureFlagsConfigService.getFeature('cScanThickness');

    this.canShowAnalysisMenu = !!this._featureFlagsConfigService.getFeature('snr')
      || !!this._featureFlagsConfigService.getFeature('canAlignData')
      || !!this._featureFlagsConfigService.getFeature('canStitchData');

    this._communicationService.context.zoneStatisticModeEnabled = this._featureFlagsConfigService.getFeature('zoneStatistics')
      && this._preference.instant('ZoneStatisticModeEnabled');

    this.canShowZoneHistogram = this._communicationService.context.zoneStatisticModeEnabled;

    if (this._featureFlagsConfigService.getFeature('zoneStatistics')) {
      this._subscription.add(
        this._preference.get('ZoneStatisticModeEnabled').subscribe((enabled) => {
          this.canShowZoneHistogram = enabled;
        }),
      );
    }
  }

  private initEventListener(): void {
    document.addEventListener('wheel', (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    }, { passive: false });

    if (this._config.isProduction) {
      document.addEventListener('contextmenu', (event: MouseEvent) => {
        if (!event.ctrlKey) {
          event.preventDefault();
        }
      });
    }
  }

  private initStatusBar(): void {
    let encoderPositionStatusBar: EncoderPositionStatusBar;

    this._communicationService.context.inspectionModeController.inspectionMode$.pipe(
      filter(inspectionMode => inspectionMode === InspectionMode.SETUP || inspectionMode === InspectionMode.INSPECTION),
      take(1),
    ).subscribe(() => {
      encoderPositionStatusBar = new EncoderPositionStatusBar(
        this._communicationService.communication.encoders$,
        this._communicationService.context.inspectionModeController.inspectionMode$,
        this._scannerService,
        this._paramsInfoService,
      );

      this._statusbar.register(encoderPositionStatusBar);

      const maxScanSpeedStatusBar: MaxScanSpeedStatusBar = new MaxScanSpeedStatusBar(
        this._communicationService.communication.maximumAcquisitionSpeed$,
        this._communicationService.context.inspectionModeController.inspectionMode$,
        this._paramsInfoService,
      );

      this._statusbar.register(maxScanSpeedStatusBar);
    });

    const connectionStatusBar: ConnectionStatusBar = new ConnectionStatusBar('DPU');
    const inspectionModeStatusBar: InspectionModeStatusBar = new InspectionModeStatusBar(
      this._communicationService.context.inspectionModeController.inspectionMode$,
      this._featureFlagsConfigService);
    const devicesConnectionStatusBar: DevicesConnectionStatusBar = new DevicesConnectionStatusBar('Devices', this._devicesService);
    this._statusbar.register(new FileNameStatusBar(), inspectionModeStatusBar, connectionStatusBar);

    this._subscription.add(
      this._featureFlagsConfigService.availableFeatures$.subscribe((features) => {
        if (features.includes('inspection')) {
          this._statusbar.register(devicesConnectionStatusBar);
        }
      }),
    );

    if (this._config.isCloudConnected) {
      const apiConnectionStatusBar: ConnectionStatusBar = new ConnectionStatusBar('Cloud');
      this._statusbar.register(apiConnectionStatusBar);

      /**
       * API connection status
      */
      this._subscription.add(
        this._cloud.status$.subscribe((status) => {
          apiConnectionStatusBar.active = status;
        }),
      );
    }

    /**
     * DPU connection status
     */
    this._subscription.add(
      this._communicationService.communication.connected$.subscribe((connected) => {
        this._state$.patch('OpenFileDialog', {
          enabled: connected,
        });
        connectionStatusBar.active = connected;
      }),
    );

    /**
     * Remotely controlled status
     */
    const remoteControlledStatusBar: RemoteControlledStatusBar = new RemoteControlledStatusBar();
    this._subscription.add(
      this._remoteControlService.isRemotelyControlled$.subscribe((controlled) => {
        if (controlled === true) {
          this._statusbar.register(remoteControlledStatusBar);
        } else {
          this._statusbar.unregister(remoteControlledStatusBar);
        }
      }),
    );
  }

  private initDpuFile(): void {
    this._subscription.add(
      this._state$.get(DpuStateKey.DPU_LOAD_FILE).subscribe((loadedFile) => {
        this.isFileLoaded = !!loadedFile.value;
      }),
    );
  }

  private initPalette(): void {
    concat(
      this._paletteService.loadPalette('rainbow'),
      this._paletteService.loadPalette('thickness'),
      this._paletteService.loadPalette('gray'),
      this._paletteService.loadPalette('monochrome_yellow'),
    ).pipe(
      toArray(),
      take(1),
    ).subscribe(palettes =>
      this._paletteService.addDefaultColorPalettes(palettes),
    );
  }

  private initLayoutAndCanvas(): void {
    /**
     * Layout manager
     */
    this._subscription.add(
      this._layoutManagerService.layoutManagerIsActive$.subscribe((layoutManagerIsActive) => {
        this.layoutManagerIsActive = layoutManagerIsActive;
        this._detector.detectChanges();
      }),
    );
  }

  private getVersion(): string {
    const initials: string = this._featureFlagsConfigService.appConfig.appInitials;
    const version: string = this._config.version.windView;
    const dpuVersion: string = !this._config.isProduction ? `, DPU-${utDpuVersion}` : '';

    return `${initials}-${version}${dpuVersion}`;
  }

  private getInitials(): string {
    const profile = this._userService?.profile();

    if (this._config.isEvidentConnect && profile?.avatar.type === 'initials') {
      return profile.avatar.initials;
    }

    return '';
  }

  private getAvatarUrl(): string | undefined {
    const profile = this._userService?.profile();

    if (this._config.isEvidentConnect && profile?.avatar.type === 'image') {
      return profile.avatar.src;
    }

    return undefined;
  }
}
